import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Link } from "react-router-dom";
import {
  Boxes,
  Wallet,
  AlertTriangle,
  ClipboardList,
  TrendingUp,
  ArrowRight,
  BarChart3,
  TrendingDown,
} from "lucide-react";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

const Stat = ({ icon: Icon, label, value, subtitle, tone = "amber", testId }) => (
  <div
    className="surface-card rounded-xl p-6 relative overflow-hidden group"
    data-testid={testId}
  >
    <div className={`absolute top-0 right-0 w-24 h-24 rounded-full blur-3xl opacity-30 ${
      tone === "red" ? "bg-red-500" : tone === "blue" ? "bg-blue-500" : "bg-amber-500"
    }`} />
    <div className="flex items-center justify-between mb-4 relative">
      <div className="text-xs uppercase tracking-widest text-slate-500 font-mono-plex">
        {label}
      </div>
      <Icon className={`w-4 h-4 ${
        tone === "red" ? "text-red-400" : tone === "blue" ? "text-blue-400" : "text-amber-500"
      }`} />
    </div>
    <div className="font-display text-4xl font-bold tracking-tight">{value}</div>
    {subtitle && (
      <div className="text-xs text-slate-500 mt-1 font-mono-plex">{subtitle}</div>
    )}
  </div>
);

const chartTooltipStyle = {
  background: "#12141a",
  border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: 8,
  fontSize: 12,
  color: "#f8fafc",
  fontFamily: "IBM Plex Mono, monospace",
};

const formatDay = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return iso;
  }
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/dashboard/stats"), api.get("/dashboard/trends")])
      .then(([s, t]) => {
        setStats(s.data);
        setTrends(t.data);
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-8" data-testid="dashboard-root">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-amber-500 font-mono-plex mb-2">
            Command Center
          </div>
          <h1 className="font-display text-4xl font-bold">Dashboard</h1>
          <p className="text-slate-500 text-sm mt-1">
            Live overview of stock, sales and open orders.
          </p>
        </div>
        <div className="hidden sm:flex gap-3">
          <Link
            to="/inventory"
            data-testid="cta-add-inventory"
            className="btn-amber rounded-md px-4 py-2 text-sm"
          >
            Add inventory
          </Link>
          <Link
            to="/ai-suggestions"
            data-testid="cta-ai-insights"
            className="rounded-md px-4 py-2 text-sm border border-white/10 hover:bg-white/[0.03] text-slate-200"
          >
            AI Insights
          </Link>
        </div>
      </div>

      {loading || !stats ? (
        <div className="text-slate-500 font-mono-plex text-sm">Loading stats…</div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Stat
              icon={Boxes}
              label="SKUs tracked"
              value={stats.total_items}
              subtitle="active inventory items"
              testId="stat-items"
            />
            <Stat
              icon={Wallet}
              label="Stock value"
              value={`$${stats.total_value.toLocaleString()}`}
              subtitle="on-hand value"
              testId="stat-value"
            />
            <Stat
              icon={AlertTriangle}
              label="Low stock"
              value={stats.low_stock_count}
              subtitle="at or below reorder"
              tone="red"
              testId="stat-low"
            />
            <Link to="/alerts" className="block">
              <Stat
                icon={TrendingDown}
                label="Cost alerts"
                value={stats.cost_alert_count ?? 0}
                subtitle="prices up >5%"
                tone={stats.cost_alert_count > 0 ? "red" : "blue"}
                testId="stat-cost-alerts"
              />
            </Link>
          </div>

          {/* Trend charts */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            <div className="surface-card rounded-xl p-6 lg:col-span-3" data-testid="sales-trend-panel">
              <div className="flex items-center justify-between mb-1">
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500 font-mono-plex mb-1">
                    Last 14 days
                  </div>
                  <div className="font-display text-2xl font-bold">Sales trend</div>
                </div>
                <TrendingUp className="w-4 h-4 text-emerald-400" />
              </div>
              <div className="text-xs text-slate-500 font-mono-plex mb-4">
                Daily revenue (cost basis)
              </div>
              <div className="h-64" data-testid="sales-trend-chart">
                {trends?.daily_sales?.some((d) => d.revenue > 0) ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={trends.daily_sales}>
                      <defs>
                        <linearGradient id="amberFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.5} />
                          <stop offset="100%" stopColor="#f59e0b" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                      <XAxis
                        dataKey="date"
                        tickFormatter={formatDay}
                        tick={{ fill: "#64748b", fontSize: 11, fontFamily: "IBM Plex Mono" }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        tick={{ fill: "#64748b", fontSize: 11, fontFamily: "IBM Plex Mono" }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={(v) => `$${v}`}
                        width={50}
                      />
                      <Tooltip
                        contentStyle={chartTooltipStyle}
                        labelFormatter={formatDay}
                        formatter={(v, k) => (k === "revenue" ? [`$${v}`, "Revenue"] : [v, k])}
                      />
                      <Area
                        type="monotone"
                        dataKey="revenue"
                        stroke="#f59e0b"
                        strokeWidth={2}
                        fill="url(#amberFill)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex flex-col items-center justify-center text-slate-500 text-center">
                    <BarChart3 className="w-8 h-8 mb-2 opacity-40" />
                    <div className="text-sm">No sales in the last 14 days yet.</div>
                    <Link to="/sales" className="text-xs text-amber-500 mt-2">
                      Log your first sale →
                    </Link>
                  </div>
                )}
              </div>
            </div>

            <div className="surface-card rounded-xl p-6 lg:col-span-2" data-testid="top-skus-panel">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-mono-plex mb-1">
                Last 30 days
              </div>
              <div className="font-display text-2xl font-bold mb-4">Top consumed</div>
              <div className="h-64" data-testid="top-skus-chart">
                {trends?.top_skus?.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={trends.top_skus}
                      layout="vertical"
                      margin={{ top: 0, right: 12, left: 0, bottom: 0 }}
                    >
                      <CartesianGrid stroke="rgba(255,255,255,0.05)" horizontal={false} />
                      <XAxis
                        type="number"
                        tick={{ fill: "#64748b", fontSize: 10, fontFamily: "IBM Plex Mono" }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        type="category"
                        dataKey="name"
                        tick={{ fill: "#94a3b8", fontSize: 11 }}
                        axisLine={false}
                        tickLine={false}
                        width={90}
                      />
                      <Tooltip
                        contentStyle={chartTooltipStyle}
                        formatter={(v) => [v, "Units"]}
                        cursor={{ fill: "rgba(245,158,11,0.06)" }}
                      />
                      <Bar dataKey="quantity" fill="#f59e0b" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex flex-col items-center justify-center text-slate-500 text-center">
                    <BarChart3 className="w-8 h-8 mb-2 opacity-40" />
                    <div className="text-sm">No consumption data yet.</div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="surface-card rounded-xl p-6 lg:col-span-2" data-testid="low-stock-panel">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500 font-mono-plex mb-1">
                    At-risk items
                  </div>
                  <div className="font-display text-2xl font-bold">Low stock</div>
                </div>
                <Link
                  to="/purchase-orders"
                  className="text-xs text-amber-500 hover:text-amber-400 flex items-center gap-1"
                  data-testid="link-generate-pos"
                >
                  Generate POs <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
              {stats.low_stock_items.length === 0 ? (
                <div className="text-slate-500 text-sm py-8 text-center">
                  All stock levels look healthy ✦
                </div>
              ) : (
                <div className="space-y-2">
                  {stats.low_stock_items.map((it) => (
                    <div
                      key={it.id}
                      className="flex items-center justify-between px-3 py-2.5 rounded-md hover:bg-white/[0.02] border border-transparent hover:border-white/5"
                    >
                      <div>
                        <div className="text-sm font-medium">{it.name}</div>
                        <div className="text-xs text-slate-500 font-mono-plex">
                          {it.category} · {it.unit}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-mono-plex text-sm text-red-400">
                          {it.current_stock} / {it.reorder_point}
                        </div>
                        <div className="text-xs text-slate-500 font-mono-plex">
                          stock / reorder
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="surface-card rounded-xl p-6" data-testid="sales-panel">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-mono-plex mb-1">
                Last 7 days
              </div>
              <div className="font-display text-2xl font-bold mb-4">Sales</div>
              <div className="flex items-baseline gap-3">
                <div className="font-display text-5xl font-bold text-amber-500">
                  ${stats.sales_7d.toLocaleString()}
                </div>
                <TrendingUp className="w-5 h-5 text-emerald-400" />
              </div>
              <div className="text-xs text-slate-500 mt-2 font-mono-plex">
                {stats.sales_7d_count} transactions logged
              </div>
              <Link
                to="/sales"
                data-testid="link-log-sale"
                className="mt-6 inline-flex items-center gap-1 text-sm text-amber-500 hover:text-amber-400"
              >
                Log a sale <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
